
import Vue from "vue";
import Component from "vue-class-component";
import filters from "@/filters";
import {
  CourseAssessmentQuestionTypeEnum,
  CourseAssessmentTypeEnum,
  CourseSectionEnum,
  EnumHelper,
} from "@/constants/enum.constant";
import { Prop, Watch } from "vue-property-decorator";
import ModalStartTest from "@/views/my-course/ModalStartTest.vue";
import ModalRetakeTest from "@/views/my-course/ModalRetakeTest.vue";
import toast from "@/functions/toast.function";
import { Action, State } from "vuex-class";
import CourseAssessmentMultipleChoices from "@/views/my-course/assessment/CourseAssessmentMultipleChoices.vue";
import CourseAssessmentCheckbox from "@/views/my-course/assessment/CourseAssessmentCheckbox.vue";
import CourseAssessmentScale from "@/views/my-course/assessment/CourseAssessmentScale.vue";
import pako from "pako";
import DoughnutChart from "@/components/charts/DoughnutChart.vue";

@Component({
  components: {
    CourseAssessmentScale,
    CourseAssessmentCheckbox,
    CourseAssessmentMultipleChoices,
    ModalStartTest,
    ModalRetakeTest,
    DoughnutChart,
  },
  filters,
})
export default class MyCourseAssessment extends Vue {
  @State('user', { namespace: 'auth' })
  private user!: any;

  @Action("submitAssessment", { namespace: "myCourse" })
  private submitAssessment: any;

  private chartKey = 0;
  private chartData: any = {
    labels: ["Correct", "Incorrect"],
    datasets: [{
      backgroundColor: ["#2CCD70", "#E5E5E5"],
      data: [0, 100],
    }],
  };
  private scoreShow = 0;

  private chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 80,
  };

  @Prop({ default: null })
  private course!: any;
  @Prop({ default: CourseSectionEnum.PRE_TEST })
  private section!: number;
  @Prop({ default: 1 })
  private step!: number;
  @Prop({ default: null })
  private assessment!: any;
  @Prop({ default: null })
  private transaction!: any;

  private sections = EnumHelper(CourseSectionEnum);
  private data: any = null;
  private scoreIndex = 0;

  @Watch("assessment", { immediate: true, deep: true })
  private onAssessment() {
    this.data = this.assessment;
  }

  @Watch("section", { immediate: true, deep: true })
  private onSection() {
    this.scoreIndex = 0;
    this.chartKey++;
    this.scoreShow = 0;
    this.chartData.datasets[0].data = [0, 100];
  }

  private get isFirstStep() {
    return this.step === 1;
  }
  private get isLastStep() {
    return this.step === this.questions?.length;
  }
  private get isPreTest() {
    return this.assessment?.type === CourseAssessmentTypeEnum.PRE_TEST;
  }
  private get isPostTest() {
    return this.assessment?.type === CourseAssessmentTypeEnum.POST_TEST;
  }
  private get index() {
    return this.step - 1;
  }
  private get questions() {
    return this.data?.sections.map((section) => section.questions).flat() || [];
  }
  private get question() {
    return this.questions[this.index];
  }
  private get isResult() {
    return (
      this.data?.result !== null ||
      parseInt(this.transaction?.post_test_attempt_remaining) === 0 ||
      this.step === this.questions?.length + 1
    );
  }
  private get value() {
    if (!this.data?.result) return 0;
    return parseInt(this.data?.result).toFixed(2);
  }
  private get score() {
    const totalQuestions = this.questions.length;
    const currentQuestions = this.questions.slice(0, this.step);
    let score = 0;

    for (const question of currentQuestions) {
      switch (question.type) {
        case CourseAssessmentQuestionTypeEnum.MULTIPLE_CHOICES:
          if (question?.answer && question?.answer.is_correct) score++;
          break;
        case CourseAssessmentQuestionTypeEnum.SCALE:
          if (question?.answer) score++;
          break;
      }
    }

    const checkboxes = currentQuestions.filter(
      (question) => question.type === CourseAssessmentQuestionTypeEnum.CHECKBOX
    );
    for (const checkbox of checkboxes) {
      const count = checkbox.answers.filter(
        (answer) => answer.is_correct
      ).length;
      const answers = (checkbox?.answer || []).length;
      const correctAnswers = (checkbox?.answer || []).filter(
        (answer) => answer.is_correct
      ).length;

      if (answers === correctAnswers && correctAnswers === count) score++;
    }

    return ((score / totalQuestions) * 100).toFixed(2);
  }

  private mounted() {
    setTimeout(() => {
      if (this.isFirstStep && !this.isResult)
        this.$bvModal.show("modal-start-test");
    }, 300);

    this.loadAnswersFromStorage();
  }

  private isShortText(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.SHORT_TEXT;
  }
  private isLongText(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.LONG_TEXT;
  }
  private isMultipleChoices(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.MULTIPLE_CHOICES;
  }
  private isCheckbox(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.CHECKBOX;
  }
  private isDropdown(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.DROPDOWN;
  }
  private isFile(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.FILE;
  }
  private isScale(data: any) {
    return data.type === CourseAssessmentQuestionTypeEnum.SCALE;
  }

  private setAnswer(sectionIndex, questionIndex, answer) {
    if (this.isCheckbox(this.question)) {
      const data =
        this.data?.sections[sectionIndex].questions[questionIndex].answer || [];

      if (data.map((answer) => answer.id).includes(answer.id)) {
        data.splice(data.map((answer) => answer.id).indexOf(answer.id), 1);
      } else {
        data.push(answer);
      }

      answer = data;
    }
    this.$set(
      this.data.sections[sectionIndex].questions[questionIndex],
      "answer",
      answer
    );

    this.saveAnswersToStorage();
  }

  private prev() {
    if (this.step === 1) return;
    this.$emit("nextStep", this.step - 1);
  }

  private next() {
    if (this.isResult) {
      this.$emit("nextStep", this.questions.length + 2);
      return false;
    }
    if (!this.data?.sections[0].questions[this.index].answer) {
      toast.warning("Please answer the question first.");
      return false;
    }
    this.scoreIndex++;
    this.$emit("nextStep", this.step + 1);
    this.updateChartData();
  }

  private retake() {
    if (this.transaction?.post_test_attempt_remaining > 0)
      this.$bvModal.show("modal-start-test");
    else this.$bvModal.show("modal-retake-test");
  }

  private startRetake() {
    this.course.post_test.result = null;
    this.$emit("retake");
    // Check if there is chartData in localStorage and load it, otherwise reset to default
    const key = this.getStorageKey();
    const savedAnswers = localStorage.getItem(key);
    if (savedAnswers) {
      try {
        const savedData = JSON.parse(savedAnswers);
        if (savedData.chartData) {
          this.chartData.datasets[0].data = savedData.chartData;
        } else {
          this.chartData.datasets[0].data = [0, 100];
        }
        if (savedData.scoreShow !== undefined) {
          this.scoreShow = savedData.scoreShow;
        } else {
          this.scoreShow = 0;
        }
      } catch (error) {
        this.chartData.datasets[0].data = [0, 100];
        this.scoreShow = 0;
      }
    } else {
      this.chartData.datasets[0].data = [0, 100];
      this.scoreShow = 0;
    }
    this.chartKey++;
  }

  private updateChartData() {
    this.scoreShow = Number(this.score);
    this.chartData.datasets[0].data = [Number(this.score), 100 - Number(this.score)];
    this.chartKey++;
    this.saveAnswersToStorage();
  }

  private submit() {
    if (!this.data?.sections[0].questions[this.index].answer) {
      toast.warning("Please answer the question first.");
      return false;
    }
    this.updateChartData();
    const formData = new FormData();
    formData.append("course_assessment_id", this.data?.id);
    formData.append(
      "sections",
      new Blob([
        pako.deflateRaw(
          unescape(decodeURIComponent(JSON.stringify(this.data?.sections)))
        ),
      ])
    );
    this.submitAssessment({
      params: formData,
      success: () => {
        this.$emit("refresh");
        this.$emit("nextStep", this.step + 1);
        localStorage.removeItem(this.getStorageKey());
      },
    });
  }

  private getStorageKey() {
    const courseId = this.course?.id;
    const assessmentType = this.isPreTest ? 'pre_test' : 'post_test';
    const userId = this.user?.id;
    return `course_assessment_${courseId}_${assessmentType}_user_${userId}`;
  }

  private saveAnswersToStorage() {
    const key = this.getStorageKey();
    const answers = this.data?.sections?.map(section => ({
      sectionId: section.id,
      questions: section.questions.map(question => ({
        questionId: question.id,
        answer: question.answer
      }))
    }));

    // Also save chart data and score
  const dataToSave = {
    answers,
    chartData: this.chartData.datasets[0].data,
    scoreShow: this.scoreShow
  };
    
    localStorage.setItem(key, JSON.stringify(dataToSave));
  }

  private loadAnswersFromStorage() {
    const key = this.getStorageKey();
    const savedAnswers = localStorage.getItem(key);

    if (savedAnswers) {
      try {
        const savedData = JSON.parse(savedAnswers);
      
        // Restore answers to the data structure using $set for reactivity
        if (savedData.answers) {
          savedData.answers.forEach(savedSection => {
            const sectionIndex = this.data?.sections?.findIndex(s => s.id === savedSection.sectionId);
            if (sectionIndex !== -1) {
              savedSection.questions.forEach(savedQuestion => {
                const questionIndex = this.data.sections[sectionIndex].questions.findIndex(q => q.id === savedQuestion.questionId);
                if (questionIndex !== -1) {
                  // Use $set to ensure Vue reactivity
                  this.$set(
                    this.data.sections[sectionIndex].questions[questionIndex],
                    'answer',
                    savedQuestion.answer
                  );
                }
              });
            }
          });
        }

        // Restore chart data
        if (savedData.chartData) {
          this.chartData.datasets[0].data = savedData.chartData;
          this.chartKey++; // Force chart re-render
        }
        
        // Restore score display
        if (savedData.scoreShow !== undefined) {
          this.scoreShow = savedData.scoreShow;
        }
        
        // Force a reactivity update by triggering a change
        this.$forceUpdate();
      } catch (error) {
        console.error('Error loading saved answers:', error);
      }
    }
  }
}
